import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { useAuthorization } from "@common/providers";
import {
  useGetProjectByIdQuery,
  useListProjectPricingsQuery,
} from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import Show from "./Show/Show";
import { ProductPricingRow } from "./types/ProductPricing";

interface Props {
  project_id: string;
}

const ProductPricingV2Mspa: React.FC<Props> = ({ project_id }) => {
  const projectId = project_id;
  const { accountSlug } = useSelector((state: RootState) => state.slug);

  const { data: project, isLoading: projectLoading } = useGetProjectByIdQuery({
    id: projectId as any,
    slug: accountSlug,
    include: ["project-products"],
  });

  const { data: pricingsData, isLoading: pricingsLoading } =
    useListProjectPricingsQuery({
      slug: accountSlug,
      id: Number(projectId),
    });

  const authorized = useAuthorization();

  if (!authorized || projectLoading || pricingsLoading || !project) {
    return <ScopeStackSpinner />;
  }

  const currencyUnit =
    (project?.data?.attributes as any)?.["field-labels"]?.currency_unit || "$";

  const projectProducts: any[] =
    (project as any)?.included?.filter(
      (item: any) => item.attributes?.active === true
    ) || [];

  const materialPricings: any[] =
    (pricingsData?.data as any[])?.filter(
      (item: any) => item.attributes?.["service-type"] === "materials"
    ) || [];

  const getExtCost = (productId: string): number => {
    return materialPricings
      .filter((p: any) => {
        const numericId = p.id.split("-")[2];
        return numericId === productId;
      })
      .map((p: any) => parseFloat(p.attributes["hardware-cost"]) || 0)
      .reduce((prev, curr) => prev + curr, 0);
  };

  const getExtPrice = (productId: string): number => {
    return materialPricings
      .filter((p: any) => {
        const numericId = p.id.split("-")[2];
        return numericId === productId;
      })
      .map((p: any) => parseFloat(p.attributes["hardware-price"]) || 0)
      .reduce((prev, curr) => prev + curr, 0);
  };

  const products: ProductPricingRow[] = projectProducts.map((item: any) => {
    const attr = item.attributes;
    const id = String(item.id);
    const quantity = attr.quantity || 0;
    const extCost = getExtCost(id);
    const extPrice = getExtPrice(id);
    const blendedCost = quantity > 0 ? extCost / quantity : 0;
    const blendedPrice = quantity > 0 ? extPrice / quantity : 0;

    return {
      id,
      name: attr.name || "",
      quantity,
      mfrPrice: attr["list-price"] || "0.0",
      discount: attr["vendor-discount"] || "0",
      blendedCost,
      markup: attr["markup"] || "0",
      blendedPrice,
      extCost,
      extPrice,
    };
  });

  return (
    <Show
      products={products}
      currencyUnit={currencyUnit}
      isLoading={pricingsLoading}
    />
  );
};

export default ProductPricingV2Mspa;
